import { t } from '../i18n';
import { backButton as tgBackButton, haptic as tgHaptic } from '../telegram-mini-app';

/**
 * Minimal modal manager. One stack, one root (`#modal-root`).
 *
 * Behavior:
 *   - Open: appends a `<div class="modal-shell">` containing the modal HTML
 *   - ESC closes the topmost modal
 *   - Click outside the modal body closes it
 *   - Body scroll-lock while at least one modal is open
 *   - Focus trap inside the topmost modal (basic — first focusable on open,
 *     restore last focus on close)
 *   - Modals can stack (A opens B; closing B restores focus to A)
 */

export interface ModalDef {
  /** Stable id used as `data-modal="<id>"` and for `close(id)` lookup. */
  id: string;
  /** HTML for the inner body. The shell + close button are added by the manager. */
  render: () => string;
  /** Called after the modal is in the DOM. Return a cleanup function. */
  onMount?: (root: HTMLElement) => (() => void) | void;
  /** Called after close, after cleanup. */
  onClose?: () => void;
  /** ARIA labelledby — id of an element inside `render()` that names the modal. */
  labelledBy?: string;
  /** Class added to the inner panel. Lets routes reuse legacy CSS (e.g. `auth-modal`). */
  panelClass?: string;
}

type Open = {
  def: ModalDef;
  shell: HTMLElement;
  cleanup: (() => void) | null;
  /**
   * Telegram Mini App BackButton frame unmount. Pushed in open(), popped
   * in close(). The TG BackButton (top of stack semantics) on tap closes
   * THIS modal — which is the right TG-native UX: BackButton always
   * backs out of the topmost UI layer first.
   */
  tgBackUnmount: (() => void) | null;
  prevFocus: Element | null;
};

const stack: Open[] = [];
let root: HTMLElement | null = null;
let docListenersAttached = false;

function getRoot(): HTMLElement {
  if (root) return root;
  root = document.getElementById('modal-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'modal-root';
    document.body.appendChild(root);
  }
  return root;
}

function topmost(): Open | undefined {
  return stack[stack.length - 1];
}

function focusFirstIn(el: HTMLElement): void {
  // Prefer the first MEANINGFUL focusable element (input / link / non-close
  // button). The close button is just a dismiss action and visually lives
  // in the top-right corner — focusing it on open makes the orange a11y
  // ring flash there every time, which reads like the modal is "dirty".
  // The user can still tab to close (or press Esc).
  const focusables = el.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  let first: HTMLElement | null = null;
  for (const node of focusables) {
    if (node.classList.contains('modal-close')) continue;
    first = node;
    break;
  }
  // Fallback: nothing else focusable (rare — a modal with only a close
  // button) → focus the panel itself so Tab still works.
  (first ?? el).focus();
}

function lockScroll(): void {
  if (stack.length === 1) {
    document.body.style.overflow = 'hidden';
  }
}
function unlockScroll(): void {
  if (stack.length === 0) {
    document.body.style.overflow = '';
  }
}

function attachDocListeners(): void {
  if (docListenersAttached) return;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const top = topmost();
      if (top) {
        e.preventDefault();
        close(top.def.id);
      }
    }
  });
  
  // FIX a11y: focus trap — keep Tab/Shift+Tab inside the topmost modal
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const top = topmost();
    if (!top) return;
    const panel = top.shell.querySelector<HTMLElement>('[data-modal-panel]');
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !panel.contains(active))) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first?.focus();
    }
  });
  docListenersAttached = true;
}

export function open(def: ModalDef): void {
  attachDocListeners();
  const r = getRoot();

  // Idempotency by id: if a modal with this id is already in the stack
  // (e.g. caller invoked open() twice in rapid succession via a race
  // condition), close the old one first. Without this, two shells
  // accumulate in the DOM and a single close() only removes one — the
  // user is left staring at a "stuck" modal.  Operator caught this on
  // 2026-05-09 поздний вечер +9: «авторизовался по Face ID, а welcome-
  // back экран не пропал».
  const existingIdx = stack.findIndex((s) => s.def.id === def.id);
  if (existingIdx >= 0) {
    close(def.id);
  }

  // Build shell
  const shell = document.createElement('div');
  shell.className = 'modal-shell';
  shell.dataset.modal = def.id;
  shell.setAttribute('role', 'dialog');
  shell.setAttribute('aria-modal', 'true');
  if (def.labelledBy) shell.setAttribute('aria-labelledby', def.labelledBy);

  shell.innerHTML = `
<div class="modal-overlay" data-modal-overlay></div>
<div class="modal-panel ${def.panelClass ?? ''}" data-modal-panel>
  <button class="modal-close" data-modal-close aria-label="${t('modal.close')}">×</button>
  ${def.render()}
</div>`;

  // Click outside (overlay) closes
  shell.querySelector('[data-modal-overlay]')?.addEventListener('click', () => close(def.id));
  shell.querySelector('[data-modal-close]')?.addEventListener('click', () => {
    // TG-native feel: explicit close-via-X feels like a physical tap.
    // Outside Telegram this is a no-op.
    tgHaptic.tapLight();
    close(def.id);
  });

  const prevFocus = document.activeElement;
  r.appendChild(shell);

  // Telegram BackButton frame: tap → close this modal. Pushed AFTER the
  // shell mounts so the order in the global BackButton stack matches the
  // visual modal stack. Inside the modal the topmost frame wins, so
  // BackButton dismisses the most-recent modal first. Outside Telegram
  // this is a no-op.
  const tgBackUnmount = tgBackButton.push(() => {
    tgHaptic.tapLight();
    close(def.id);
  });

  const entry: Open = { def, shell, cleanup: null, tgBackUnmount, prevFocus };
  stack.push(entry);
  lockScroll();

  const panel = shell.querySelector<HTMLElement>('[data-modal-panel]');
  if (panel) {
    if (def.onMount) {
      const c = def.onMount(panel);
      if (typeof c === 'function') entry.cleanup = c;
    }
    focusFirstIn(panel);
  }
}

export function close(id?: string): void {
  const idx = id ? stack.findIndex((s) => s.def.id === id) : stack.length - 1;
  if (idx < 0) return;
  const [entry] = stack.splice(idx, 1);
  if (!entry) return;
  try {
    entry.cleanup?.();
  } catch {
    /* swallow — best-effort */
  }
  // Pop the TG BackButton frame this modal pushed. Order matters: do
  // this BEFORE shell.remove so any TG event still in-flight has a
  // valid frame target. No-op outside TG.
  try {
    entry.tgBackUnmount?.();
  } catch {
    /* best-effort */
  }
  entry.shell.remove();
  if (entry.prevFocus instanceof HTMLElement) entry.prevFocus.focus();
  entry.def.onClose?.();
  unlockScroll();
}

export function closeAll(): void {
  while (stack.length) close();
}

export function isOpen(id: string): boolean {
  return stack.some((s) => s.def.id === id);
}

export const ModalManager = { open, close, closeAll, isOpen };
